/**
 * Broker Account Management service (Broker Integration — Phase 1: Broker
 * Account Management).
 *
 * Talks only to this app's own FastAPI backend (`/api/v1/broker-accounts/*`).
 * Every call is implicitly scoped to the current authenticated user by the
 * backend (via the JWT) — there is no `user_id` parameter anywhere here,
 * same convention as `portfolioService`. Nothing here places orders, reads
 * positions/holdings/funds, or does anything resembling live/paper trading
 * — see backend `app.services.broker_account_service`'s module docstring
 * for the full list of what's deliberately out of scope for this phase.
 *
 * SECURITY: no method here, and no response type it returns, ever carries
 * `access_token`/`refresh_token` — the backend's `BrokerAccountRead` schema
 * never serializes either field (see `app.schemas.broker_account`).
 */

import api from "@/services/api";
import type {
  BrokerAccountListResponse,
  BrokerAccountRead,
  BrokerConnectRequest,
  BrokerDisconnectRequest,
  BrokerLoginInfoResponse,
  BrokerName,
  BrokerReconnectRequest,
  BrokerSetDefaultRequest,
  DhanInstrumentImportResponse,
  DhanInstrumentMasterAutoImportStatusResponse,
} from "@/types";

export const brokerAccountService = {
  /** `GET /broker-accounts` — every broker account the current user has
   * ever connected (including disconnected ones — cards for those still
   * render, just in the 🔴 Disconnected state with a "Reconnect" button). */
  async list(): Promise<BrokerAccountListResponse> {
    const { data } = await api.get<BrokerAccountListResponse>("/broker-accounts");
    return data;
  },

  /** `GET /broker-accounts/{broker_name}/login-url` — how the Connect
   * dialog should start this broker's flow (redirect URL and/or
   * manual-paste fields). Safe to call for a broker the user hasn't
   * connected yet. */
  async getLoginInfo(brokerName: BrokerName): Promise<BrokerLoginInfoResponse> {
    const { data } = await api.get<BrokerLoginInfoResponse>(`/broker-accounts/${brokerName}/login-url`);
    return data;
  },

  /** `POST /broker-accounts/connect` — first-time connect OR reconnecting
   * an already-known broker (the backend upserts by (user, broker)). */
  async connect(body: BrokerConnectRequest): Promise<BrokerAccountRead> {
    const { data } = await api.post<BrokerAccountRead>("/broker-accounts/connect", body);
    return data;
  },

  /** `POST /broker-accounts/reconnect` — same underlying connect, but keyed
   * off an existing card's `id` rather than a broker name. */
  async reconnect(body: BrokerReconnectRequest): Promise<BrokerAccountRead> {
    const { data } = await api.post<BrokerAccountRead>("/broker-accounts/reconnect", body);
    return data;
  },

  /** `POST /broker-accounts/disconnect` — clears the stored session but
   * keeps the row (client ID / account name survive for next time). */
  async disconnect(body: BrokerDisconnectRequest): Promise<BrokerAccountRead> {
    const { data } = await api.post<BrokerAccountRead>("/broker-accounts/disconnect", body);
    return data;
  },

  /** `PUT /broker-accounts/default` — marks one CONNECTED account as the
   * default; the backend clears any other row's default flag in the same
   * request. Only a connected account can be made default. */
  async setDefault(body: BrokerSetDefaultRequest): Promise<BrokerAccountRead> {
    const { data } = await api.put<BrokerAccountRead>("/broker-accounts/default", body);
    return data;
  },

  /** `GET /broker-accounts/status` — re-runs a live health check against
   * every connected account and returns the refreshed list. An unreachable
   * broker leaves that account's previous status untouched (never guessed
   * into "disconnected") — see backend `refresh_status`'s docstring. */
  async refreshStatus(): Promise<BrokerAccountListResponse> {
    const { data } = await api.get<BrokerAccountListResponse>("/broker-accounts/status");
    return data;
  },

  /** `POST /broker-accounts/admin/import-dhan-instrument-master` — admin
   * only. Uploads Dhan's own published DETAILED scrip master CSV
   * (https://images.dhan.co/api-data/api-scrip-master-detailed.csv,
   * downloaded by the admin themselves) so live Dhan orders can resolve a
   * real numeric `securityId` — see backend `app.services.dhan_instrument_
   * master_import_service` for the full rationale. Only index-option
   * (OPTIDX) rows are kept. Re-run whenever Dhan's file changes (new
   * expiries listed). */
  async importDhanInstrumentMaster(file: File): Promise<DhanInstrumentImportResponse> {
    const formData = new FormData();
    formData.append("file", file);
    const { data } = await api.post<DhanInstrumentImportResponse>(
      "/broker-accounts/admin/import-dhan-instrument-master",
      formData,
      { headers: { "Content-Type": "multipart/form-data" } }
    );
    return data;
  },

  /** `GET /broker-accounts/admin/dhan-instrument-master-status` — admin
   * only, read-only. Data Operations page "last run" status/timestamp
   * (manual upload above OR the automated daily fetch — 2026-09-19), plus
   * whether that automated fetch is enabled and what time it's scheduled
   * for, so an admin can see whether a re-run is even needed. */
  async getDhanInstrumentMasterStatus(): Promise<DhanInstrumentMasterAutoImportStatusResponse> {
    const { data } = await api.get<DhanInstrumentMasterAutoImportStatusResponse>(
      "/broker-accounts/admin/dhan-instrument-master-status"
    );
    return data;
  },
};

export default brokerAccountService;
